import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Upload,
  Loader2,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import * as api from '../lib/esignApi';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function Verify() {
  const { envelopeId } = useParams();
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null); // { match: bool } | { match: null, hash_unavailable: true }
  const [verifyError, setVerifyError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getPublicEnvelopeMeta(envelopeId);
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) setMetaError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [envelopeId]);

  const handleFile = useCallback((f) => {
    setResult(null);
    setVerifyError(null);
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setVerifyError('Please upload a PDF.');
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      setVerifyError('File too large (25 MB max).');
      return;
    }
    setFile(f);
  }, []);

  const handleVerify = useCallback(async () => {
    if (!file) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      const r = await api.verifyEnvelopePdf(envelopeId, file);
      setResult(r);
    } catch (err) {
      setVerifyError(err.message);
    } finally {
      setVerifying(false);
    }
  }, [file, envelopeId]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      const f = e.dataTransfer.files?.[0];
      if (f) handleFile(f);
    },
    [handleFile]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (metaError || !meta?.envelope) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-5">
              <ShieldAlert className="w-7 h-7 text-gray-500" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Envelope not found</h1>
            <p className="text-sm text-gray-500 leading-relaxed">
              Either this envelope does not exist, or it has not yet been completed.
              Only completed envelopes are publicly verifiable.
            </p>
            <p className="text-xs text-gray-400 font-mono mt-6 break-all">{envelopeId}</p>
          </div>
        </div>
      </div>
    );
  }

  const envelope = meta.envelope;
  const signers = meta.signers || [];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 px-4 py-10">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Verify document</h1>
              <p className="text-sm text-gray-500">Powered by Scrivar</p>
            </div>
          </div>

          {/* Envelope summary */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">{envelope.subject}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Sender</span>
                <span className="text-gray-900 font-medium text-right">{envelope.sender_name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Sent</span>
                <span className="text-gray-900 text-right">{formatDate(envelope.sent_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Completed</span>
                <span className="text-gray-900 text-right">{formatDate(envelope.completed_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Signers</span>
                <span className="text-gray-900 text-right">{signers.length}</span>
              </div>
            </div>

            {signers.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Signed by
                </p>
                <ul className="space-y-1.5">
                  {signers.map((s, i) => (
                    <li key={i} className="text-sm flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900 font-medium">{s.name}</span>
                      <span className="text-gray-400 text-xs">on {formatDate(s.signed_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Upload + verify */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Verify a copy</h3>
            <p className="text-sm text-gray-500 mb-4">
              Upload the PDF you received. We compute its SHA-256 hash locally on our
              server and compare it to the one recorded at signing. Your file is never stored.
            </p>

            {!envelope.has_hash && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  This envelope was completed before hash tracking was added. Automatic
                  verification is unavailable — contact the sender to confirm authenticity.
                </span>
              </div>
            )}

            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${
                dragging
                  ? 'border-indigo-400 bg-indigo-50'
                  : file
                    ? 'border-indigo-300 bg-indigo-50/40'
                    : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              }`}
            >
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] || null)}
              />
              {file ? (
                <>
                  <FileText className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(file.size / 1024).toFixed(1)} KB · Click to replace
                  </p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">
                    Drag a PDF here or click to browse
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PDF only · up to 25 MB</p>
                </>
              )}
            </div>

            {verifyError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {verifyError}
              </div>
            )}

            <button
              onClick={handleVerify}
              disabled={!file || verifying || !envelope.has_hash}
              className="mt-4 w-full bg-indigo-600 text-white py-2.5 rounded-lg hover:bg-indigo-700 transition font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying…
                </>
              ) : (
                'Verify document'
              )}
            </button>

            {/* Result badge */}
            {result && result.match === true && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                <ShieldCheck className="w-6 h-6 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Document verified.</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    This is the authentic signed copy — byte-for-byte identical to the one
                    completed on {formatDate(envelope.completed_at)}.
                  </p>
                </div>
              </div>
            )}
            {result && result.match === false && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <XCircle className="w-6 h-6 text-red-500 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Document does not match.</p>
                  <p className="text-xs text-red-700 mt-0.5">
                    The PDF you uploaded differs from the completed original. It may have been
                    modified after signing — or this may not be the right envelope.
                  </p>
                </div>
              </div>
            )}
            {result && result.match === null && result.hash_unavailable && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Hash not available</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    This envelope predates hash tracking and cannot be automatically verified.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="text-center">
            <a
              href="https://scrivar.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition"
            >
              Learn more about Scrivar
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      <footer className="py-4 text-center text-xs text-gray-400 select-none">
        Powered by{' '}
        <a
          href="https://scrivar.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-500 hover:text-indigo-600 transition"
        >
          Scrivar
        </a>
      </footer>
    </div>
  );
}
